import { useState, useEffect } from 'react'
import {
  Modal, Stack, TextInput, Group, Button, Alert, Text, ThemeIcon,
} from '@mantine/core'
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { gitlabApi } from '@/api/gitlab'
import { formatApiError } from '@/api/errors'

interface GitLabConfigModalProps {
  opened: boolean
  onClose: () => void
  tenantId: number
  onSaved?: () => void
}

const GITLAB_URL_REGEX = /^https?:\/\/.+\/.+/

function validateUrl(url: string): { valid: boolean; groupPath?: string } {
  if (!url || url.length < 10) return { valid: false }
  if (!GITLAB_URL_REGEX.test(url)) return { valid: false }
  try {
    const parsed = new URL(url)
    const groupPath = parsed.pathname.replace(/^\//, '').replace(/\/$/, '')
    if (!groupPath) return { valid: false }
    return { valid: true, groupPath }
  } catch {
    return { valid: false }
  }
}

export function GitLabConfigModal({ opened, onClose, tenantId, onSaved }: GitLabConfigModalProps) {
  const [groupUrl, setGroupUrl] = useState('')
  const [branch, setBranch] = useState('main')
  const [urlValid, setUrlValid] = useState(false)
  const [groupPath, setGroupPath] = useState<string>()

  const saveMutation = useMutation({
    mutationFn: () => gitlabApi.saveConfig(tenantId, {
      gitlab_group_url: groupUrl,
      default_branch: branch,
      enabled: true,
    }),
    onSuccess: () => {
      onSaved?.()
      onClose()
    },
  })

  useEffect(() => {
    const result = validateUrl(groupUrl)
    setUrlValid(result.valid)
    setGroupPath(result.groupPath)
  }, [groupUrl])

  useEffect(() => {
    if (!opened) {
      setGroupUrl('')
      setBranch('main')
      setUrlValid(false)
      setGroupPath(undefined)
      saveMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const errorMessage = saveMutation.error
    ? formatApiError(saveMutation.error, 'Erro ao salvar configuração')
    : null

  return (
    <Modal opened={opened} onClose={onClose} title="Configurar GitLab" size="md">
      <Stack gap="md">
        <TextInput
          label="URL do Grupo GitLab"
          placeholder="https://gitlab.example.com/my-group"
          value={groupUrl}
          onChange={(e) => setGroupUrl(e.currentTarget.value)}
          rightSection={
            urlValid ? (
              <ThemeIcon size={20} color="green" variant="light" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
            ) : groupUrl.length >= 10 ? (
              <ThemeIcon size={20} color="red" variant="light" radius="xl">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
            ) : null
          }
        />

        {urlValid && groupPath && (
          <Text size="sm" c="green">Grupo: {groupPath}</Text>
        )}

        {!urlValid && groupUrl.length >= 10 && (
          <Text size="sm" c="red">URL inválida. Use o formato: https://gitlab.example.com/grupo</Text>
        )}

        <TextInput
          label="Branch monitorada"
          placeholder="main"
          value={branch}
          onChange={(e) => setBranch(e.currentTarget.value)}
        />

        {errorMessage && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {errorMessage}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!urlValid || !branch}
          >
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
